import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Form } from 'antd';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import client from '../../../apollo';
import { MODULES, ROUTES } from '../../../common/constants';
import { fileUpload } from '../../../common/utils';
import CommonHeader from '../../../components/CommonHeader';
import CommonPreview from '../../../components/CommonPreview';
import { CREATE_BLOG, UPDATE_BLOG } from '../graphql/Mutations';
import { BLOG, GET_BLOG_UPLOAD_URL } from '../graphql/Queries';
import BlogForm from './BlogForm';

const CreateBlog = ({ isOrganization = false }) => {
  const history = useHistory();
  const { mode, id, orgId, orgMode } = useParams();
  const [form] = Form.useForm();
  const [isView, setIsView] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const [blogImage, setBlogImage] = useState([]);
  const [iconImage, setIconImage] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState();

  const [createBlog, { loading: createLoading }] = useMutation(CREATE_BLOG);
  const [updateBlog, { loading: updateLoading }] = useMutation(UPDATE_BLOG);
  const [getBlog, { data: blogData, loading: loadBlog }] = useLazyQuery(BLOG);

  const baseRoute = `${ROUTES.ORGANIZATIONS}/${orgMode}/${orgId}${ROUTES?.BLOGS}`;

  useEffect(() => {
    if (mode === 'view') setIsView(true);
    if (id)
      getBlog({ variables: { where: { id } }, fetchPolicy: 'network-only' });
  }, []);

  useEffect(() => {
    if (blogData?.blog) {
      const data = blogData?.blog;
      form.setFieldsValue({
        title: data?.title,
        link: data?.link,
        description: data?.description,
        image: data?.image ? [{ url: data?.image }] : [],
        icon: data?.icon ? [{ url: data?.icon }] : [],
      });
      setBlogImage(data?.image ? [{ url: data?.image }] : []);
      setIconImage(data?.icon ? [{ url: data?.icon }] : []);
    }
  }, [blogData]);

  const handleShowPrompt = () => {
    setIsDisabled(false);
  };

  const onCancelEdit = () => {
    setIsView(true);
    history?.push(baseRoute);
  };

  const onEdit = () => {
    setIsView(false);
    history?.push(`${baseRoute}/edit/${id}`);
  };

  async function getBlogSignedUrl({ file, query, companyId, type }) {
    if (!query) throw new Error('GraphQL query document is required');
    if (!companyId) throw new Error('companyId is required');
    if (!file) throw new Error('file is required');
    if (!type) throw new Error('type is required');

    const { name } = file;
    const res = await client.query({
      query,
      variables: {
        data: {
          companyId,
          fileName: name,
          type,
        },
      },
      fetchPolicy: 'no-cache',
    });
    return res.data;
  }

  const onFinish = async (values) => {
    const isEdit = Boolean(id);

    let imageKey = null;
    let iconKey = null;

    const dataToSend = {
      title: values?.title,
      link: values?.link || null,
      description: values?.description || null,
      image: null,
      icon: null,
    };
    if (!isEdit) {
      dataToSend.companyId = orgId;
    }

    if (values?.image?.[0]?.originFileObj) {
      const file = values.image[0].originFileObj;
      const res = await getBlogSignedUrl({
        file,
        type: 'IMAGE',
        companyId: orgId,
        query: GET_BLOG_UPLOAD_URL,
      });
      const signedImageUrl = res?.getBlogAssetsUploadSignedUrl?.signedUrl;
      imageKey = res?.getBlogAssetsUploadSignedUrl?.key;
      await fileUpload(signedImageUrl, file);
      dataToSend.image = imageKey;
    } else if (blogImage?.[0]?.url) {
      dataToSend.image = blogImage[0].url;
    }

    if (values?.icon?.[0]?.originFileObj) {
      const file = values.icon[0].originFileObj;
      const res = await getBlogSignedUrl({
        file,
        type: 'ICON',
        companyId: orgId,
        query: GET_BLOG_UPLOAD_URL,
      });
      const signedIconUrl = res?.getBlogAssetsUploadSignedUrl?.signedUrl;
      iconKey = res?.getBlogAssetsUploadSignedUrl?.key;
      await fileUpload(signedIconUrl, file);
      dataToSend.icon = iconKey;
    } else if (iconImage?.[0]?.url) {
      dataToSend.icon = iconImage[0].url;
    }

    try {
      if (isEdit) {
        delete dataToSend.order;
        await updateBlog({
          variables: { where: { id }, data: dataToSend },
          onCompleted: onCancelEdit,
        });
      } else {
        await createBlog({
          variables: { data: dataToSend },
          onCompleted: onCancelEdit,
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating/updating blog:', err);
    }
  };

  const renderHeaderRightContent = () =>
    isView ? (
      <Button className="common-button" size="middle" onClick={onEdit}>
        Edit
      </Button>
    ) : (
      <>
        <Button
          type="primary"
          className="common-button mr-16"
          size="middle"
          onClick={onCancelEdit}
        >
          Cancel
        </Button>
        <Button
          className="common-button"
          size="middle"
          onClick={form.submit}
          loading={createLoading || updateLoading}
          disabled={createLoading || updateLoading || isDisabled}
        >
          Save
        </Button>
      </>
    );

  const heading = id ? `${mode} Blog` : 'Add Blog';

  return (
    <>
      <CommonPreview
        visible={previewVisible}
        setVisible={setPreviewVisible}
        image={previewImage}
        setImage={setPreviewImage}
      />
      <CommonHeader
        heading={isOrganization ? MODULES.MASTER : heading}
        customLink={ROUTES.MASTER}
      >
        {!isOrganization && renderHeaderRightContent()}
      </CommonHeader>
      <Card
        className={
          isOrganization
            ? 'full-height-without-nav-vertical-tabs'
            : 'full-height-without-nav'
        }
      >
        <BlogForm
          form={form}
          onFinish={onFinish}
          handleShowPrompt={handleShowPrompt}
          blogImage={blogImage}
          setBlogImage={setBlogImage}
          iconImage={iconImage}
          setIconImage={setIconImage}
          isView={isView}
          isEdit={Boolean(id)}
          loadBlog={loadBlog}
        />
      </Card>
    </>
  );
};

export default CreateBlog;
